<script setup lang="ts">
/**
 * DetailPage 四分区主体（F10，从 DetailPage 拆出以满足文件 ≤300 行硬限）。
 *
 * 渲染 content-layout：左侧 `<DetailSidebar>` + 右侧 main-col（视频 / 正文 /
 * 附件三张 `.detail-section` 卡片）。状态分支 / 路由修正 / slug 修正留在
 * `DetailPage`，本组件纯渲染单个已命中的 Section。
 *
 * **分区空状态独立**（acceptance「四分区独立空状态」）：
 *   - sidebar 空 → 不渲染 sidebar 区
 *   - video 无 → 不渲染视频区
 *   - bodyHtml 空 → 不渲染正文区
 *   - materials 空 → MaterialList 内部空态保留附件区位置
 */
import { computed } from 'vue';
import RichTextViewer from '@/components/RichTextViewer.vue';

import DetailSidebar from '@/components/DetailSidebar.vue';
import MaterialList from '@/components/MaterialList.vue';
import { useAssetFallback } from '@/composables/use-asset-fallback';
import { copy } from '@/i18n/zh-CN';
import { hasText, posterFor } from '@/registry/sections/section-presenters';
import type { PublicSectionView } from '@/api/types';

const props = defineProps<{
  readonly section: PublicSectionView;
  readonly sections: readonly PublicSectionView[];
  readonly previewToken?: string | null;
  readonly pdfCacheVersion?: string | null;
  readonly sectionId: string;
}>();

const hasSidebar = computed(() => props.section.sidebar.length > 0);
const hasVideo = computed(() => hasText(props.section.videoUrl));
const hasBody = computed(() => hasText(props.section.bodyHtml));
const showMaterialsFirst = computed(() => {
  const materialIndex = props.section.sidebar.findIndex(
    (item) => item.sectionId === props.section.id && item.anchor === 'materials',
  );
  if (materialIndex === -1) {
    return false;
  }

  const bodyIndex = props.section.sidebar.findIndex(
    (item) => item.sectionId === props.section.id && item.anchor !== 'materials',
  );
  return bodyIndex === -1 || materialIndex < bodyIndex;
});
const videoPoster = computed(() => posterFor(props.section));
const { failed: videoFailed, handleError: handleVideoError } = useAssetFallback(
  `detail video section="${props.sectionId}"`,
);
</script>

<template>
  <div :class="[$style.layout, hasSidebar ? $style.layoutWide : null]" data-shell="content-layout">
    <DetailSidebar
      v-if="hasSidebar"
      :items="props.section.sidebar"
      :sections="props.sections"
      :preview-token="props.previewToken"
    />
    <div :class="$style.main" data-shell="main-col">
      <section
        v-if="hasVideo"
        :class="$style.detailSection"
        data-shell="detail-section"
        data-testid="detail-video"
      >
        <h2 :class="$style.sectionTitle" data-shell="section-title">{{ props.section.title }}</h2>
        <video
          v-if="!videoFailed"
          :class="$style.video"
          :poster="videoPoster"
          :src="props.section.videoUrl ?? undefined"
          controls
          preload="metadata"
          playsinline
          @error="handleVideoError"
        />
        <p v-else :class="$style.videoUnavailable" role="status">
          {{ copy.fallback.asset.videoUnavailable }}
        </p>
      </section>

      <MaterialList
        v-if="showMaterialsFirst"
        :materials="props.section.materials"
        :preview-token="props.previewToken"
        element-id="materials"
      />

      <section
        v-if="hasBody"
        :class="$style.detailSection"
        data-shell="detail-section"
        data-testid="detail-body"
      >
        <h2 :class="$style.sectionTitle" data-shell="section-title">{{ props.section.title }}</h2>
        <div :class="$style.richText">
          <RichTextViewer
            :html="props.section.bodyHtml"
            :preview-token="props.previewToken"
            :pdf-cache-version="props.pdfCacheVersion"
            as="article"
          />
        </div>
      </section>

      <MaterialList
        v-if="!showMaterialsFirst"
        :materials="props.section.materials"
        :preview-token="props.previewToken"
        element-id="materials"
      />
    </div>
  </div>
</template>

<style module>
.layout {
  max-width: var(--container-max);
  margin: 0 auto;
  padding: 0 var(--space-3);
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--space-4);
}

.layoutWide {
  max-width: var(--container-max-expanded);
}

.main {
  flex: 1 1 auto;
  min-width: 0;
}

.detailSection {
  background: var(--color-bg-primary);
  padding: var(--space-5);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-card);
  margin-bottom: var(--space-4);
}

.sectionTitle {
  display: flex;
  align-items: center;
  font-size: var(--font-size-lg);
  color: var(--color-brand-primary);
  font-weight: 700;
  border-bottom: 2px solid var(--color-border);
  padding-bottom: var(--space-2);
  margin: 0 0 var(--space-4);
}

.sectionTitle::before {
  content: '';
  display: inline-block;
  width: 6px;
  height: var(--font-size-lg);
  background: var(--color-brand-accent);
  margin-right: var(--space-2);
  border-radius: var(--radius-sm);
}

.richText {
  color: var(--color-text-primary);
  font-size: var(--font-size-base);
  line-height: var(--line-height-prose);
}

.richText :global(.rich-text strong),
.richText :global(.rich-text b) {
  color: var(--color-text-primary);
}

.video {
  width: 100%;
  border-radius: var(--radius-md);
  background: var(--color-bg-secondary);
}

.videoUnavailable {
  margin: 0;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  padding: var(--space-3);
  color: var(--color-text-muted);
  text-align: center;
}

@media (max-width: 959px) {
  .layout {
    flex-direction: column;
  }

  .main {
    width: 100%;
  }
}
</style>
